import { recordUsageEvents, type UsageContext, type UsageEvent, type UsageEventKind } from './api';
import { resolveUsageContext } from './platform';

/**
 * Client-side usage instrumentation. Tracked actions are buffered and flushed in batches to the
 * tenant-scoped `/usage` endpoint, keeping request and Cosmos-write volume low regardless of how
 * chatty the UI is. The buffer flushes on an interval, when it grows past a threshold, and whenever
 * the tab is hidden or unloaded (with `keepalive` so in-flight batches survive teardown).
 *
 * This is best-effort telemetry: it never throws into callers and silently no-ops when the browser
 * lacks the APIs it needs (e.g. during tests / SSR).
 */

const FLUSH_INTERVAL_MS = 30_000;
const MAX_BUFFER = 50;

let buffer: UsageEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let listenersBound = false;
let sessionContext: UsageContext | null = null;

function hasWindow(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * The session's access context (host/platform), resolved lazily on first use and cached — it does not
 * change within a session, so we resolve it once and attach it to every flushed batch. Deferring the
 * resolution lets the Teams SDK finish initializing so the host surface is known.
 */
function getSessionContext(): UsageContext {
  if (!sessionContext) {
    sessionContext = resolveUsageContext();
  }
  return sessionContext;
}

function flush(keepalive = false): void {
  if (buffer.length === 0) return;
  const batch = buffer;
  buffer = [];
  void recordUsageEvents(batch, getSessionContext(), keepalive);
}

function ensureStarted(): void {
  if (!hasWindow() || listenersBound) return;
  listenersBound = true;

  flushTimer = setInterval(() => flush(false), FLUSH_INTERVAL_MS);

  // Flush what we have when the user navigates away or backgrounds the tab. `visibilitychange` +
  // `pagehide` are the reliable pair on modern browsers; both use keepalive so the batch is not lost.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  window.addEventListener('pagehide', () => flush(true));
}

/** Records a single usage event of the given kind. Multiple events are batched before sending. */
export function trackUsage(kind: UsageEventKind, count = 1): void {
  if (!hasWindow()) return;
  ensureStarted();
  buffer.push({ kind, occurredAt: new Date().toISOString(), count });
  if (buffer.length >= MAX_BUFFER) flush(false);
}

/** Records that the app was opened. Call once per session load. */
export function trackVisit(): void {
  trackUsage('visit');
}

/** Immediately sends any buffered events. Exposed mainly for tests and explicit teardown. */
export function flushUsage(): void {
  flush(false);
}

/** Tears down timers/listeners and clears the buffer. Intended for tests. */
export function resetUsageForTests(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  buffer = [];
  listenersBound = false;
  sessionContext = null;
}
